/**
 * @fileoverview Saved-view switcher (MOR-696 Stage 2)
 *
 * Lets a practitioner save the current task surface configuration (filters +
 * view type + grouping + sort) as a named personal view and switch between
 * their saved views. Views are personal (owner-gated server-side) and scoped
 * to either a single job board (`scope: "job"`) or the cross-job workspace
 * inbox (`scope: "org"`).
 *
 * Pure presentational state lives here; persistence rides the
 * `api.tasks.views` CRUD. The `filters` <-> wire conversion keeps the
 * client-side `TaskFilterState` and the server `taskViewFilters` aligned.
 */

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { toast } from "sonner";
import { Bookmark, Check, Pencil, Plus, Star, Trash2 } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type {
  TaskViewGroupBy,
  TaskViewScope,
  TaskViewSortBy,
} from "@cvx/tasks/taskViews/schema";

import type { TaskFilterState } from "./task-filter-bar.helpers";
import { filtersFromWire, filtersToWire } from "./saved-view-switcher.helpers";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";

/** A board/list view configuration the switcher saves + restores. */
export interface TaskViewConfig {
  filters: TaskFilterState;
  viewType: "board" | "list";
  groupBy: TaskViewGroupBy;
  /** Secondary board grouping (swimlanes within columns); "none" ⇒ off. */
  subGroupBy: TaskViewGroupBy;
  sortBy: TaskViewSortBy;
}

export function SavedViewSwitcher({
  scope,
  orgId,
  jobId,
  current,
  onApply,
}: {
  scope: TaskViewScope;
  orgId: Id<"organizations">;
  jobId?: Id<"jobs">;
  current: TaskViewConfig;
  onApply: (config: TaskViewConfig) => void;
}) {
  const { data: views } = useQuery(
    convexQuery(api.tasks.views.listByScope, {
      scope,
      ...(jobId ? { jobId } : {}),
    }),
  );

  const createView = useMutation(api.tasks.views.create);
  const updateView = useMutation(api.tasks.views.update);
  const removeView = useMutation(api.tasks.views.remove);
  const setDefaultView = useMutation(api.tasks.views.setDefault);

  const [activeViewId, setActiveViewId] = useState<Id<"taskViews"> | null>(
    null,
  );
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");

  const activeView = (views ?? []).find((v) => v._id === activeViewId);

  const applyView = (view: NonNullable<typeof views>[number]) => {
    setActiveViewId(view._id);
    onApply({
      filters: filtersFromWire(view.filters),
      // `calendar` views fall back to list on the task surface.
      viewType: view.viewType === "board" ? "board" : "list",
      groupBy: view.groupBy,
      subGroupBy: view.subGroupBy ?? "none",
      sortBy: view.sortBy,
    });
  };

  // Auto-apply the owner's default view once, on first load, when nothing is
  // active yet. Scoped per (scope, jobId) via a key so navigating between
  // jobs re-applies the relevant default.
  const appliedDefaultFor = useRef<string | null>(null);
  useEffect(() => {
    if (!views) return;
    const key = `${scope}:${jobId ?? "org"}`;
    if (appliedDefaultFor.current === key) return;
    const def = views.find((v) => v.isDefault);
    appliedDefaultFor.current = key;
    if (def && !activeViewId) applyView(def);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [views, scope, jobId]);

  const handleToggleDefault = async (
    view: NonNullable<typeof views>[number],
  ) => {
    try {
      await setDefaultView({ viewId: view._id, isDefault: !view.isDefault });
      toast.success(
        view.isDefault
          ? `“${view.name}” is no longer your default`
          : `“${view.name}” is now your default view`,
      );
    } catch (err) {
      toast.error(
        extractErrorMessage(err, "Could not update the default view."),
      );
    }
  };

  const handleSaveNew = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const id = await createView({
        orgId,
        scope,
        ...(scope === "job" && jobId ? { jobId } : {}),
        name: trimmed,
        viewType: current.viewType,
        groupBy: current.groupBy,
        subGroupBy: current.subGroupBy,
        sortBy: current.sortBy,
        filters: filtersToWire(current.filters),
      });
      setActiveViewId(id);
      setSaveOpen(false);
      setName("");
      toast.success(`Saved view “${trimmed}”`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not save the view."));
    }
  };

  const handleUpdateView = async (view: NonNullable<typeof views>[number]) => {
    try {
      await updateView({
        viewId: view._id,
        patch: {
          viewType: current.viewType,
          groupBy: current.groupBy,
          subGroupBy: current.subGroupBy,
          sortBy: current.sortBy,
          filters: filtersToWire(current.filters),
        },
      });
      setActiveViewId(view._id);
      toast.success(`“${view.name}” updated with the current view`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not update the view."));
    }
  };

  const handleDelete = async (viewId: Id<"taskViews">) => {
    try {
      await removeView({ viewId });
      if (activeViewId === viewId) setActiveViewId(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not delete the view."));
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-9">
            <Bookmark className="size-3.5" />
            {activeView ? activeView.name : "Saved views"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Saved views</DropdownMenuLabel>
          {(views ?? []).length === 0 && (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              No saved views yet.
            </p>
          )}
          {(views ?? []).map((view) => (
            <DropdownMenuItem
              key={view._id}
              className="flex items-center gap-2"
              onSelect={(e) => {
                e.preventDefault();
                applyView(view);
              }}
            >
              <Check
                className={cn(
                  "size-3.5",
                  activeViewId === view._id ? "opacity-100" : "opacity-0",
                )}
              />
              <span className="flex-1 truncate">{view.name}</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={
                      view.isDefault
                        ? `Unset ${view.name} as default`
                        : `Set ${view.name} as default`
                    }
                    className={cn(
                      "rounded p-0.5",
                      view.isDefault
                        ? "text-amber-500"
                        : "text-muted-foreground hover:text-amber-500",
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleToggleDefault(view);
                    }}
                  >
                    <Star
                      className={cn(
                        "size-3.5",
                        view.isDefault && "fill-amber-500",
                      )}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {view.isDefault ? "Default view" : "Set as default"}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Update ${view.name} with the current view`}
                    className="rounded p-0.5 text-muted-foreground hover:text-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleUpdateView(view);
                    }}
                  >
                    <Pencil className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  Update with current filters &amp; sort
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Delete ${view.name}`}
                    className="rounded p-0.5 text-muted-foreground hover:text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDelete(view._id);
                    }}
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Delete view</TooltipContent>
              </Tooltip>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="gap-2"
            onSelect={(e) => {
              e.preventDefault();
              setSaveOpen(true);
            }}
          >
            <Plus className="size-3.5" />
            Save current view
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Save view</DialogTitle>
          </DialogHeader>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="View name"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleSaveNew();
            }}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleSaveNew()}
              disabled={!name.trim()}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
